import {ChangeDetectorRef, Component, HostListener, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgbPaginationModule} from '@ng-bootstrap/ng-bootstrap';
import {Router} from '@angular/router';
import {ThDateDisplayPipe} from '../../../shared/pipes/th-date-display.pipe';
import {resolveTotalElements} from '../../../shared/commons/pagination.util';
import {ArticleService} from './article.service';
import {ArticleDetailDto, ArticleListItemDto} from '../../../../generated-model/models';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';

interface StatusTab {
  key: 'all' | 'published' | 'unpublished' | 'expired';
  label: string;
}

interface SortOption {
  label: string;
  field: string;
  dir: string;
}

const SORT_OPTIONS: SortOption[] = [
  {label: 'เผยแพร่ล่าสุด', field: 'publishUp', dir: 'desc'},
  {label: 'เก่าที่สุด', field: 'publishUp', dir: 'asc'},
  {label: 'ยอดเข้าชมมากที่สุด', field: 'hits', dir: 'desc'},
  {label: 'ยอดเข้าชมน้อยที่สุด', field: 'hits', dir: 'asc'},
  {label: 'ชื่อบทความ ก–ฮ', field: 'title', dir: 'asc'},
];

const STATUS_TABS: StatusTab[] = [
  {key: 'all', label: 'ทั้งหมด'},
  {key: 'published', label: 'เผยแพร่'},
  {key: 'unpublished', label: 'ไม่เผยแพร่'},
  {key: 'expired', label: 'หมดอายุ'},
];

const AVA_COLORS = ['#0f7c73', '#3a6a93', '#6b54a3', '#b06a2c', '#a8456b'];

function getAuthorColor(name: string): string {
  let h = 0;
  for (const c of name) {
    h = (h * 31 + c.charCodeAt(0)) | 0;
  }
  return AVA_COLORS[Math.abs(h) % AVA_COLORS.length];
}

@Component({
  selector: 'app-article',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPaginationModule, ThDateDisplayPipe, SearchIconComponent],
  templateUrl: './article-list.component.html',
  styleUrl: './article-list.component.scss',
})
export class ArticleListComponent implements OnInit {
  items: ArticleListItemDto[] = [];
  displayedItems: ArticleListItemDto[] = [];
  allItems: ArticleListItemDto[] = [];
  selectedArticle: ArticleDetailDto | null = null;
  searchQuery = '';
  statusFilter: 'all' | 'published' | 'unpublished' | 'expired' = 'all';
  isLoading = false;
  isDetailLoading = false;
  page = 1;
  pageSize = 10;
  collectionSize = 0;
  totalElements = 0;
  pageSizes = [10, 25, 50];
  sortIdx = 0;
  selectedIds = new Set<number>();
  sortMenuOpen = false;
  ppMenuOpen = false;
  toastMessage: string | null = null;
  toastVisible = false;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  @HostListener('document:mousedown')
  onDocumentClick(): void {
    this.sortMenuOpen = false;
    this.ppMenuOpen = false;
  }

  readonly statusTabs = STATUS_TABS;
  readonly sortOptions = SORT_OPTIONS;

  get currentSort(): SortOption {
    return SORT_OPTIONS[this.sortIdx];
  }

  get tabCounts(): Record<string, number> {
    const items = this.allItems.length > 0 ? this.allItems : this.items;
    return {
      all: this.collectionSize,
      published: items.filter(i => i.state === 1).length,
      unpublished: items.filter(i => i.state === 0).length,
      expired: 0,
    };
  }

  constructor(
    private readonly articleService: ArticleService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router
  ) {
  }

  ngOnInit(): void {
    this.load();
    this.loadAllForCounts();
  }

  private loadAllForCounts(): void {
    this.articleService.getArticles(0, 10000, '', null).subscribe({
      next: (data) => {
        this.allItems = data.content ?? [];
      },
    });
  }

  load(): void {
    this.isLoading = true;
    const stateParam = this.mapFilterToState();
    const sort = this.currentSort;
    this.articleService.getArticles(this.page - 1, this.pageSize, this.searchQuery, stateParam, sort.field, sort.dir).subscribe({
      next: (data) => {
        this.items = data.content ?? [];
        this.displayedItems = this.items;
        this.totalElements = resolveTotalElements(data);
        this.collectionSize = this.totalElements;
        this.isLoading = false;

        if (this.selectedArticle?.id) {
          const stillVisible = this.displayedItems.some(item => item.id === this.selectedArticle?.id);
          if (!stillVisible) {
            this.selectedArticle = null;
          }
        }
        if (!this.selectedArticle && this.displayedItems.length > 0) {
          this.openDetail(this.displayedItems[0]);
        } else {
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.items = [];
        this.displayedItems = [];
        this.selectedArticle = null;
        this.collectionSize = 0;
        this.totalElements = 0;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private mapFilterToState(): number | null {
    switch (this.statusFilter) {
      case 'published':
        return 1;
      case 'unpublished':
        return 0;
      default:
        return null;
    }
  }

  onSearchChange(): void {
    this.page = 1;
    this.load();
  }

  onPageSizeChange(): void {
    this.page = 1;
    this.load();
  }

  setStatusFilter(key: 'all' | 'published' | 'unpublished' | 'expired'): void {
    this.statusFilter = key;
    this.page = 1;
    this.clearSelection();
    this.load();
  }

  setSort(idx: number): void {
    this.sortIdx = idx;
    this.sortMenuOpen = false;
    this.page = 1;
    this.load();
  }

  toggleSortDir(field: string): void {
    if (this.currentSort.field === field) {
      const nextIdx = SORT_OPTIONS.findIndex(s => s.field === field && s.dir !== this.currentSort.dir);
      if (nextIdx >= 0) {
        this.setSort(nextIdx);
      }
    } else {
      const defaultIdx = SORT_OPTIONS.findIndex(s => s.field === field);
      if (defaultIdx >= 0) {
        this.setSort(defaultIdx);
      }
    }
  }

  toggleSortMenu(): void {
    this.sortMenuOpen = !this.sortMenuOpen;
  }

  closeSortMenu(): void {
    this.sortMenuOpen = false;
  }

  togglePpMenu(): void {
    this.ppMenuOpen = !this.ppMenuOpen;
  }

  closePpMenu(): void {
    this.ppMenuOpen = false;
  }

  openDetail(item: ArticleListItemDto): void {
    if (!item.id) {
      return;
    }
    this.isDetailLoading = true;
    this.articleService.getArticleById(item.id).subscribe({
      next: (article) => {
        this.selectedArticle = article;
        this.isDetailLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.selectedArticle = null;
        this.isDetailLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  isSelected(item: ArticleListItemDto): boolean {
    return this.selectedIds.has(item.id);
  }

  toggleSelect(id: number): void {
    const next = new Set(this.selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.selectedIds = next;
  }

  toggleSelectAll(): void {
    const pageIds = this.displayedItems.map(i => i.id);
    const allSelected = pageIds.every(id => this.selectedIds.has(id));
    const next = new Set(this.selectedIds);
    if (allSelected) {
      pageIds.forEach(id => next.delete(id));
    } else {
      pageIds.forEach(id => next.add(id));
    }
    this.selectedIds = next;
  }

  get isAllPageSelected(): boolean {
    const pageIds = this.displayedItems.map(i => i.id);
    return pageIds.length > 0 && pageIds.every(id => this.selectedIds.has(id));
  }

  clearSelection(): void {
    this.selectedIds = new Set();
  }

  getStateLabel(state: number | null | undefined): string {
    switch (state) {
      case 1:
        return 'เผยแพร่';
      case 0:
        return 'ไม่เผยแพร่';
      case 2:
        return 'Archived';
      case -2:
        return 'Trash';
      default:
        return '-';
    }
  }

  getStateClass(state: number | null | undefined): string {
    switch (state) {
      case 1:
        return 'bg-success';
      case 0:
        return 'bg-secondary';
      case 2:
        return 'bg-warning text-dark';
      case -2:
        return 'bg-danger';
      default:
        return 'bg-light text-dark';
    }
  }

  getArticleContent(article: ArticleDetailDto | null): string {
    if (!article) {
      return '';
    }
    return `${article.introtext ?? ''}${article.fulltext ?? ''}`;
  }

  openEditPage(item: ArticleListItemDto): void {
    if (!item.id) {
      return;
    }
    void this.router.navigate(['/campaigns/articles', item.id, 'edit']);
  }

  openPublicPage(item: ArticleListItemDto): void {
    if (typeof window === 'undefined') {
      return;
    }
    const identifier = `${item.alias ?? ''}`.trim() || `${item.id ?? ''}`.trim();
    if (!identifier) {
      return;
    }
    const targetUrl = new URL(`/ui/public/articles/${encodeURIComponent(identifier)}`, window.location.origin).toString();
    window.open(targetUrl, '_blank', 'noopener');
  }

  openCreatePage(): void {
    void this.router.navigate(['/campaigns/articles/new']);
  }

  toggleArticleStatus(item: ArticleListItemDto, newStateId: number): void {
    const id = item.id;
    this.articleService.updateArticle(id, {
      title: item.title ?? '',
      alias: item.alias ?? '',
      state: newStateId,
    } as any).subscribe({
      next: () => {
        this.showToast(newStateId === 1 ? 'เผยแพร่บทความแล้ว' : 'ยกเลิกการเผยแพร่แล้ว');
        this.load();
        this.loadAllForCounts();
      },
      error: () => {
        this.showToast('ไม่สามารถเปลี่ยนสถานะได้');
      }
    });
  }

  bulkPublish(): void {
    const ids = [...this.selectedIds];
    this.showToast(`เผยแพร่ ${ids.length} บทความแล้ว`);
    this.clearSelection();
  }

  bulkUnpublish(): void {
    const ids = [...this.selectedIds];
    this.showToast(`ยกเลิกการเผยแพร่ ${ids.length} บทความ`);
    this.clearSelection();
  }

  bulkDelete(): void {
    const ids = [...this.selectedIds];
    this.showToast(`ลบ ${ids.length} บทความแล้ว`);
    this.clearSelection();
  }

  duplicateArticle(item: ArticleListItemDto): void {
    this.showToast(`ทำสำเนา "${(item.title ?? '').slice(0, 24)}..."`);
  }

  deleteArticle(item: ArticleListItemDto): void {
    this.showToast(`ลบบทความ "${(item.title ?? '').slice(0, 24)}..."`);
  }

  previewArticle(item: ArticleListItemDto): void {
    this.showToast('เปิดดูตัวอย่างบทความ');
  }

  getAuthorColor(name: string | null | undefined): string {
    return getAuthorColor(name ?? '');
  }

  getInitial(name: string | null | undefined): string {
    const n = name ?? '';
    return n.length > 0 ? n[0] : '?';
  }

  getHitDisplay(hits: number | null | undefined): string {
    return (hits ?? 0).toLocaleString('en-US');
  }

  private showToast(msg: string): void {
    this.toastMessage = msg;
    this.toastVisible = true;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastTimer = setTimeout(() => {
      this.toastVisible = false;
      this.toastTimer = null;
    }, 2400);
  }
}
